import React from 'react';
import PropTypes from 'prop-types';

import cx from 'classnames';

import withTheme from 'core/hocs/withTheme';

import themePropTypes from 'core/utils/prop-types/theme';
import { resolveScopedStyles } from 'core/utils/styled-jsx';
import isNotEmptyReactElementContent from 'core/utils/content';

import styles from './index.styl';

function resolveStateFromProps(props) {
  if (props.state !== 'auto') return props.state;
  if (props.disabled) return 'disabled';
  return null;
}

/**
 * @author Pavel Povalyaev <p.povalyaev@rambler-co.ru>
 */
function Button(props) {
  const {
    background,
    minWidth,
    children,
    inactive,
    onClick,
    round,
    typeAttribute,
    radius,
    type,
    sheet, // eslint-disable-line
    size,
    state,
    className,
    height,
    dataQa,
    theme: {
      animations: {
        hover: hoverAnimation,
      },
      controls: {
        button: buttonAtoms,
        button: {
          const: {
            sizes: buttonSizeAtoms,
          },
        },
      },
    },
    ...attributes
  } = props;

  const stateFromProps = resolveStateFromProps({ state, size, type, ...attributes });

  const content = [].concat(children).filter(isNotEmptyReactElementContent);

  const buttonStyles = {
    ...background && { background },
  };

  if (typeof minWidth === 'number' || typeof minWidth === 'string') {
    buttonStyles.minWidth = minWidth;
  }

  const buttonAtomsIdleState = buttonAtoms[type][stateFromProps || 'idle'];
  const buttonAtomsHoverState = buttonAtoms[type][stateFromProps || 'hover'];
  const buttonAtomsActiveState = buttonAtoms[type][stateFromProps || 'active'];
  const hasBoxShadow = [buttonAtomsIdleState, buttonAtomsHoverState, buttonAtomsActiveState]
    .some(stateProps => stateProps.boxShadow);

  const scopeBoxShadow = hasBoxShadow && resolveScopedStyles(
    <scope>
      <style jsx>{`
        ._boxShadow
          transition ${ ['color', 'border', 'background', 'box-shadow'].map(name => name + ' ' + hoverAnimation)}
          box-shadow ${buttonAtomsIdleState.boxShadow || 'none'}

          &:hover
            box-shadow ${buttonAtomsHoverState.boxShadow || 'none'}

          &:active
            box-shadow ${buttonAtomsActiveState.boxShadow || 'none'}
      `}</style>
    </scope>
  );

  return (
    <button
      {...attributes}
      className={cx(
        className,
        styles.button,
        'button',
        round && 'round',
        buttonAtoms.const.textTransform === 'uppercase' && styles.letterSpacing,
        scopeBoxShadow && scopeBoxShadow.wrapClassNames('_boxShadow')
      )}
      {...dataQa && { 'data-qa': dataQa + '-button' }}
      style={buttonStyles}
      onClick={inactive ? undefined : onClick}
      type={typeAttribute}
    >
      <style jsx>{`
        .button
          cursor ${attributes.disabled || inactive ? 'default' : 'pointer'}
          font ${buttonSizeAtoms[size].font}
          min-width ${buttonSizeAtoms[size].width}
          min-height ${height || buttonSizeAtoms[size].height}
          padding ${buttonSizeAtoms[size].padding}
          border-radius ${radius || buttonAtoms.const.radius}
          text-transform ${buttonAtoms.const.textTransform}
          color ${buttonAtomsIdleState.color}
          border ${buttonAtomsIdleState.border}
          background ${buttonAtomsIdleState.background}
          transition ${ ['color', 'border', 'background'].map(name => name + ' ' + hoverAnimation)}
          &:hover
            color ${buttonAtomsHoverState.color}
            border ${buttonAtomsHoverState.border}
            background ${buttonAtomsHoverState.background}
          &:active
            color ${buttonAtomsActiveState.color}
            border ${buttonAtomsActiveState.border}
            background ${buttonAtomsActiveState.background}
          &.round
            padding 0
            min-width 0
            min-height 0
            border-radius 50%
      `}</style>
      {
        /**
         * Пришлось обернуть контент в span из-за того что не во всех браузерах
         * элемент `button` может быть flex контейнером.
         *
         * Подробнее: https://stackoverflow.com/a/35466231/896280
         */
      }
      <span className={styles.content}>
        {content}
      </span>
      {scopeBoxShadow && <scopeBoxShadow.styles />}
    </button>
  );
}

const sizeContract = `
  {
    font,
    width,
    height,
    padding,
  }
`;

const stateContract = `
  {
    color,
    border,
    background,
  }
`;

const typeContract = `
  {
    idle: ${stateContract},
    hover: ${stateContract},
    active: ${stateContract},
    disabled: ${stateContract},
  }
`;

Button.propTypes = {
  /**
   * Фон кнопки. Значение должно быть валидным CSS-свойством background.
   */
  background: PropTypes.string,

  /**
   * Отключает кнопку, делая её неактивной и применяя соответствующие
   * стили для отображения
   */
  disabled: PropTypes.bool,

  /**
   * Признак неактивности кнопки. Влияет на стили и обработчики событий.
   * Такое поведение нужно для использования в других компонентах.
   */
  inactive: PropTypes.bool,

  /** Определяет стиль отображения кнопки */
  type: PropTypes.string.isRequired,

  /**
   * Обработчик события клика по кнопке
   */
  onClick: PropTypes.func,

  /**
   * Отключает внутренние отступы и делает кнопку круглой.
   */
  round: PropTypes.bool,

  /**
   * Радиус границы, с указанием единиц измерения.
   * Имеет приоритет над указанным в теме.
   */
  radius: PropTypes.string,

  /**
   * Размер кнопки, предопределенный темой
   */
  size: PropTypes.string,

  /**
   * Фиксируем кнопку в одном из доступных состояний.
   * Такое поведение нужно для использования в других компонентах.
   */
  state: PropTypes.oneOf([
    'auto',
    'idle',
    'hover',
    'active',
  ]).isRequired,

  /**
   * Указываем свой класс
   */
  className: PropTypes.string,

  /** @ignore */
  theme: themePropTypes(`
    {
      animations: {
        hover,
      },
      controls: {
        button: {
          const: {
            radius,
            sizes: {
              small: ${sizeContract},
              medium: ${sizeContract},
              large: ${sizeContract},
            },
          },
          primary: ${typeContract},
          secondary: ${typeContract},
          ghost: ${typeContract},
        },
      },
    },
  `),

  /** Определяет атрибут type элемента `<button>` */
  typeAttribute: PropTypes.oneOf(['button', 'submit', 'reset']),

  /**
   * Здесь можно переопредилить минимальную ширину кнопки в инлайн стиле.
   * Строка позволяет указать значение в процентах.
   */
  minWidth: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),

  /**
   * Выстота кнопки, с указанием единиц измерения.
   * Имеет приоритет над указанным в теме.
   */
  height: PropTypes.string,

  /** @ignore */
  dataQa: PropTypes.string,
};

Button.defaultProps = {
  disabled: false,
  inactive: false,
  round: false,
  type: 'primary',
  size: 'medium',
  state: 'auto',
  typeAttribute: 'button',
};

const ButtonComponent = withTheme(Button);
ButtonComponent.displayName = 'Button';

export default ButtonComponent;

export { Button as StorybookComponent };
